import * as d3 from 'd3';
import { differenceInMinutes } from 'date-fns';
import { chain, first, forEach, groupBy, isNil, isNumber, last, map, matches, pick, sortedUniq } from 'lodash';

import { PerSensor } from '@/api/historicalData';
import { UserDisplayPreferences } from '@/api/userAccount';
import { GenericObject, getXValue, getYRangeForMultipleLines, makeColorSet } from '@/charts/helpers';
import {
    AlertLine,
    AlertSeverity,
    ColorSet,
    D3Selection,
    D3SvgSelection,
    MarginConfig,
    PointerInteractionSource,
} from '@/charts/types';
import { DateRange } from '@/store';
import { fontFamily } from '@/theme';
import { isFiniteNumber, isGreater, prettyOperator } from '@/utils';

const GRID_LINE_STROKE_WIDTH = 0.6;
const HOVERLINE_COLOR = 'steelblue';
const HOVERLINE_OPACITY = 0.3;
const HOVERLINE_STROKE_WIDTH = 8;
const SELECTION_LINE_COLOR = 'darkgray';
const SELECTION_LINE_OPACITY = 0.7;
const SELECTION_LINE_STROKE_WIDTH = 2;
const ALERT_LINE_STROKE_WIDTH = 1;
const ALERT_SEVERITY_COLORS: Record<AlertSeverity, { label: string; bg: string; line: string }> = {
    info: {
        label: '#002024',
        bg: '#E1F7FD',
        line: '#16C0E2',
    },
    warning: {
        label: '#812D00',
        bg: '#FFF3E0',
        line: '#F57C00',
    },
    error: {
        label: '#5D0701',
        bg: '#FFE5E3',
        line: '#FF7065',
    },
};
export const BAND_CHART_HOVER_COLOR = '#ddd';

export const createSvgElement = (
    containerId: string,
    options: { margin?: Partial<MarginConfig>; width?: number; height?: number } = {}
): {
    svg: D3SvgSelection;
    width: number;
    height: number;
    margin: MarginConfig;
} | null => {
    const { width = 600, height = 400 } = options;
    const margin: MarginConfig = { top: 8, right: 32, bottom: 40, left: 60, ...options.margin };
    const chartWidth = Math.max(0, width - margin.left - margin.right);
    const chartHeight = Math.max(0, height - margin.top - margin.bottom);

    const container = d3.select(`#${CSS.escape(containerId)}`);
    if (container.empty()) {
        return null;
    }

    const svg = container
        .append('svg')
        .attr('width', chartWidth + margin.left + margin.right)
        .attr('height', chartHeight + margin.top + margin.bottom)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`)
        .style('user-select', 'none')
        .style('font-family', fontFamily)
        .style('font-weight', 300);
    /*
        Note "width - margin.right" here is to allow space for alert text to the right of the chart
    */
    return { svg, width: chartWidth, height: chartHeight, margin };
};

type AddXAxisProps = {
    svg: D3SvgSelection;
    x: d3.ScaleTime<number, number>;
    width: number;
    height: number;
    showSeconds?: boolean;
    timeZone: UserDisplayPreferences['timeZone'];
};

export const addXAxisToSvg = ({ svg, x, width, height, showSeconds, timeZone }: AddXAxisProps) => {
    const timeFormat = timeZone === 'UTC' ? d3.utcFormat : d3.timeFormat;
    const timeTicks = timeZone === 'UTC' ? d3.utcTicks : d3.timeTicks;
    const min = first(x.domain())!;
    const max = last(x.domain())!;
    // if our caller thinks we're on the scale of seconds, we don't want to show dates even if we ultimately decide to round seconds on the axis labels
    const showDates = !showSeconds;
    if (differenceInMinutes(max, min) > 5) {
        // if we're more than a few minutes, the ticks will definitely be rounded to the minute and the trailing :00s aren't helpful
        showSeconds = false;
    }
    const tickCount = Math.ceil(width / (showSeconds ? 120 : 100));
    const ticks = timeTicks(min, max, tickCount);
    // Group ticks by date to get first and last tick of each day
    const firstTicksOfTheDay = !showDates
        ? new Set()
        : new Set(
              chain(ticks)
                  .sortBy((tick) => tick.getTime())
                  .groupBy(timeFormat('%Y-%m-%d'))
                  .values()
                  .map((ticksOfDay) => ticksOfDay[0].getTime())
                  .value()
          );

    const axis = svg
        .append('g')
        .attr('transform', `translate(0, ${height})`)
        .call(
            d3
                .axisBottom(x)
                .tickValues(ticks)
                .tickFormat((domainValue) => {
                    if (domainValue instanceof Date) {
                        const lines = [];
                        const timeOfDay = timeFormat('%H:%M' + (showSeconds ? ':%S' : ''))(domainValue);
                        const isFirstTickOfDay = firstTicksOfTheDay.has(domainValue.getTime());
                        if (!('00:00:00'.startsWith(timeOfDay) && isFirstTickOfDay)) {
                            // if we're exactly midnight (which is likely on a wide enough window), skip time of day if we're planning
                            // to show date
                            lines.push(timeOfDay);
                        }
                        if (isFirstTickOfDay) {
                            // if we're first tick of the day, add the date below the time if it's the middle of the day, or
                            // on its own if it's midnight
                            lines.push(timeFormat('%d %b')(domainValue));
                        }
                        return lines.join('\n');
                    }
                    console.warn('Cannot format date', domainValue);
                    return '';
                })
        );

    breakLines(
        axis.selectAll('text').attr('x', 0).attr('y', 8).style('text-anchor', 'middle').style('font-size', '12px')
    );

    return { ticks };
};

export const breakLines = (textSelection: D3Selection<d3.BaseType>) => {
    textSelection.each(function () {
        const lines = (this as SVGTextElement).textContent?.split(/\n/);
        d3.select(this).text(null);
        lines?.map((l) => d3.select(this).append('tspan').attr('x', 0).attr('dy', 14).text(l));
    });
};

export const addGridToSvg = ({
    svg,
    x,
    xTicks,
    y,
    height,
    width,
}: {
    svg: D3SvgSelection;
    x?: d3.ScaleTime<number, number>;
    xTicks?: Date[];
    y?: d3.ScaleLinear<number, number>;
    height: number;
    width: number;
}) => {
    if (x) {
        // Add vertical gridline
        svg.selectAll('xGrid')
            .data(xTicks ?? x.ticks())
            .join('line')
            .attr('x1', (d) => x(d))
            .attr('x2', (d) => x(d))
            .attr('y1', 0)
            .attr('y2', height)
            .attr('stroke', '#e0e0e0')
            .attr('stroke-width', GRID_LINE_STROKE_WIDTH);
    }
    if (y) {
        // Add horizontal gridlines
        svg.selectAll('yGrid')
            .data(y.ticks())
            .join('line')
            .attr('x1', 0)
            .attr('x2', width)
            .attr('y1', (d) => y(d))
            .attr('y2', (d) => y(d))
            .attr('stroke', '#e0e0e0')
            .attr('stroke-width', GRID_LINE_STROKE_WIDTH);
    }
};

export const linesAcrossSensors = <T extends { id: string }>(baseLines: T[], sensorPositions: string[]) =>
    baseLines.flatMap((line) =>
        sensorPositions.map((sensorPosition) => ({
            ...line,
            dataFilter: matches({ sensorPosition }),
            id: `${line.id}-${sensorPosition}`,
            sensorPosition,
            color: sensorToColor(sensorPosition),
        }))
    ) as (T & { sensorPosition: string })[];

/**
 * TODO: We get it pretty much flat like this from the API, then structure it into {sensor-1: [...], ...} for some reason.
 * We should drop the intermediate structure and this function should be reduced to its filter
 * behavior (which maybe should just get inlined at call sites). https://app.shortcut.com/sofar/story/265208
 */
export const flattenSensorPositions = <Datum extends object>(
    sensorData: PerSensor<Datum[]> | undefined,
    { filterSensorPositions }: { filterSensorPositions?: string[] } = {}
): { sensorPositions: string[]; dataset: (Datum & { sensorPosition: string })[] } => {
    // collect all sensorPositions that have nonempty datasets, and flatten data with each datum annotated with sensorPosition
    const filteredPositions = filterSensorPositions ? pick(sensorData, filterSensorPositions) : sensorData;
    const sensorPositions: string[] = [];
    const dataset: (Datum & { sensorPosition: string })[] = [];
    forEach(filteredPositions, (positionData, sensorPosition) => {
        if (positionData?.length) {
            sensorPositions.push(sensorPosition);
            positionData.forEach((datum) => dataset.push({ ...datum, sensorPosition }));
        }
    });

    return { sensorPositions, dataset };
};

export const colorsets = {
    gray: makeColorSet('#c0c0c0', '#888888'),
    blue: makeColorSet('#096ED3', '#274A72'),
    red: makeColorSet('#FF424B', '#99050C'),
    purple: makeColorSet('#EB76FF', '#9F10BC'),
    orange: makeColorSet('#F6630C', '#B67854'),
    green: makeColorSet('#90CA23', '#568204'),
    yellow: makeColorSet('#EFCF16', '#724C00'),
} as const;

const sensorColors = [
    colorsets.blue,
    colorsets.red,
    colorsets.green,
    colorsets.yellow,
    colorsets.purple,
    colorsets.orange,
] as const;

export const sensorToColor = (position: number | string): ColorSet => {
    const positionNum = isNumber(position) ? position : Number(position.replace('sensor-', ''));
    if (positionNum === 0) {
        // Not expecting anyone to ask about the bridge, but just in case
        return colorsets.gray;
    }
    const colorIndex = (positionNum - 1) % sensorColors.length;
    return sensorColors[colorIndex];
};

export type SvgSelectionAttr = Parameters<d3.Selection<SVGGElement, unknown, HTMLElement, unknown>['attr']>[1];

/**
 * If dataYRange is omitted, figures it out from the line definitions assuming simple lines.
 */
export type ChartTemplateProps = {
    dataset: GenericObject[];
    xAxisKey: keyof GenericObject;
    yAxisLabel: string;
    yAxisOptions?: {
        tickValues?: Iterable<d3.NumberValue>;
        tickFormat?: (v: d3.NumberValue) => string;
        minRange?: { yMin?: number; yMax?: number };
    };
    dataYRange?: { yMin: number; yMax: number };
    lines: { valueKey: keyof GenericObject }[];
    rangePaddingPercent?: number;
    containerId: string;
    scaleChartToFitAllAlerts?: boolean;
    alertLines?: AlertLine[];
    chartHeight?: number;
    chartWidth?: number;
    dateRange?: DateRange | null;
    timeZone: UserDisplayPreferences['timeZone'];
};

const addChartDynamicElementsToSvg = ({
    svg,
    height,
    width,
    containerId,
    options = { listeningRectBehavior: 'raise' },
}: {
    svg: D3SvgSelection;
    height: number;
    width: number;
    containerId: string;
    options?: { listeningRectBehavior?: 'lower' | 'raise' };
}) => {
    // remove the previous selectionline if it exists
    const previousSelectionLine = document.getElementById(`selectionline-${containerId}`);
    if (previousSelectionLine) {
        previousSelectionLine.remove();
    }
    const selectionLine = svg
        .append('line')
        .style('visibility', 'hidden')
        .style('stroke', SELECTION_LINE_COLOR)
        .style('stroke-width', SELECTION_LINE_STROKE_WIDTH)
        .attr('opacity', SELECTION_LINE_OPACITY)
        .attr('y1', 0)
        .attr('y2', height)
        .style('pointer-events', 'none')
        .attr('id', `selectionline-${containerId}`);

    // remove the previous hoverline if it exists
    const previousHoverLine = document.getElementById(`hoverline-${containerId}`);
    if (previousHoverLine) {
        previousHoverLine.remove();
    }
    const hoverLine = svg
        .append('line')
        .style('visibility', 'hidden')
        .style('stroke', HOVERLINE_COLOR)
        .style('stroke-width', HOVERLINE_STROKE_WIDTH)
        .attr('opacity', HOVERLINE_OPACITY)
        .attr('y1', 0)
        .attr('y2', height)
        .style('pointer-events', 'none')
        .attr('id', `hoverline-${containerId}`);

    // remove the previous listeningRect if it exists
    const previousListeningRect = document.getElementById(`listening-rect-${containerId}`);
    if (previousListeningRect) {
        previousListeningRect.remove();
    }
    const listeningRectBehaviorFn = options?.listeningRectBehavior ? options.listeningRectBehavior : 'raise';
    const listeningRect = svg
        .append('rect')
        [listeningRectBehaviorFn]()
        .attr('width', width)
        .attr('height', height)
        .style('fill-opacity', '0')
        .style('pointer-events', 'all')
        .style('stroke-opacity', '0')
        .style('z-index', '1')
        .attr('id', `listening-rect-${containerId}`);

    // remove the previous tooltip if it exists
    const previousTooltip = document.getElementById(`tooltip-${containerId}`);
    if (previousTooltip) {
        previousTooltip.remove();
    }
    // Create a tooltip div
    const tooltip = d3
        .select(`#${CSS.escape(containerId)}`)
        .append('div')
        .attr('id', `tooltip-${containerId}`)
        .style('visibility', 'hidden')
        .style('position', 'absolute')
        .style('z-index', '10')
        .style('top', '0')
        .style('left', '0')
        .style(
            'box-shadow',
            '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)'
        )
        .style('padding', '10px')
        .style('border-radius', '8px')
        .style('background-color', 'white')
        .style('white-space', 'nowrap')
        .style('font-family', fontFamily)
        .style('font-weight', 300);

    return { selectionLine, hoverLine, listeningRect, tooltip };
};

export const addHoverElementsToSvg = ({
    svg,
    height,
    width,
    containerId,
}: {
    svg: D3SvgSelection;
    height: number;
    width: number;
    containerId: string;
}) => {
    const circle = svg
        .append('circle')
        .attr('r', 0)
        .attr('fill', 'steelblue')
        .style('stroke', 'blue')
        .attr('opacity', 0.7)
        .style('pointer-events', 'none');

    const moreElements = addChartDynamicElementsToSvg({
        svg,
        height,
        width,
        containerId,
    });

    return { circle, ...moreElements };
};

/**
 * Expands the bottom of the domain by paddingMin and the top by paddingMax, where the padding is supplied in units of range.
 * Useful to push the data away from the edges of the chart by a specific number of pixels.
 */
const padDomainInRangeUnits = (
    scale: d3.ScaleLinear<number, number> | d3.ScaleTime<number, number>,
    paddingMin: number,
    paddingMax: number
) => {
    const [oldMin, oldMax] = scale.range();
    if (oldMax < oldMin) {
        paddingMin *= -1;
        paddingMax *= -1;
    }
    scale.domain([scale.invert(oldMin - paddingMin), scale.invert(oldMax + paddingMax)]);
};

const setupXandYScales = ({ height, width }: { height: number; width: number }) => {
    const x = d3.scaleTime().range([0, width]);
    const y = d3.scaleLinear().range([height, 0]);
    return { x, y };
};

/**
 * Creates a chart SVG element and appends it to the specified container.
 * Does things that need to be done only once for the entire chart:
 *    - create svg element
 *    - set up x timescale and y linear scale
 *    - define x and y domains
 *    - add x-axis
 *    - add y-axis
 *    - add y-axis label
 *    - set color of the axis, ticks and labels
 *    - add horizontal and vertical gridlines
 *    - add hover elements: hoverLine, listeningRect, tooltip
 */
export const createChartTemplate = ({
    dataset,
    xAxisKey,
    yAxisLabel,
    yAxisOptions,
    lines,
    dataYRange,
    rangePaddingPercent,
    containerId,
    scaleChartToFitAllAlerts = false,
    alertLines,
    chartHeight,
    chartWidth,
    timeZone = 'local',
    dateRange,
}: ChartTemplateProps) => {
    if (!dataset?.length) {
        return null;
    }
    // remove the previous chart if it exists
    const previousSvg = getSvgByContainerId(containerId);
    if (previousSvg) {
        previousSvg.remove();
    }
    // Set dimentions and margins for the chart
    // Create svg element (if containerId exists in the document) and append it to the chart container
    const element = createSvgElement(containerId, {
        height: chartHeight,
        width: chartWidth,
    });
    if (!element) {
        return null;
    }
    const { svg, width, height, margin } = element;
    // Set up the x and y scales
    const { x, y } = setupXandYScales({ width, height });
    /*
        Define the x and y domains.
        Note adding 30 minutes to the x-axis min and max values to make sure the chart is not cut off at the edges
        especially when the dots or bars are wide
     */
    if (!dateRange) {
        // Getting x axis range from the data to make sure the chart does not have any empty space and aligns with the dataset
        const [xDataMin, xDataMax] = d3.extent(dataset, (datum) => new Date(getXValue(datum, xAxisKey)));
        if (!xDataMin || !xDataMax) {
            console.warn(
                `Cannot create ${containerId} line chart. xDataMin or xDataMax are undefined, xDataMin: ${xDataMin}, xDataMax: ${xDataMax}`
            );
            return null;
        }
        x.domain([xDataMin, xDataMax]);
    } else {
        x.domain([dateRange.startDate, dateRange.endDate]);
    }
    padDomainInRangeUnits(x, 4, 4);

    // define x and y domains
    const [yAxisMin, yAxisMax] = getYRangeForMultipleLines({
        dataset,
        dataYRange,
        minYRange: yAxisOptions?.minRange,
        lines,
        alertLines,
        options: {
            rangePaddingPercent,
            scaleChartToFitAllAlerts,
        },
    });

    y.domain([yAxisMin, yAxisMax]);
    if (alertLines?.length) {
        // pad out a little to allow space for high > or low < labels. doing this after the initial domain is set
        // because we want to invert the padding from pixels so narrow-ranged charts don't get extra padding.
        const { greater, lesser } = groupBy(alertLines, ({ operator }) => (isGreater(operator) ? 'greater' : 'lesser'));
        padDomainInRangeUnits(y, lesser?.length ? 10 : 0, greater?.length ? 10 : 0);
    }
    // Add the x-axis
    const { ticks: xTicks } = addXAxisToSvg({ svg, x, width, height, timeZone });
    // Add the y-axis
    const yAxis = d3.axisLeft(y);
    if (yAxisOptions?.tickValues) {
        yAxis.tickValues(yAxisOptions?.tickValues);
    }
    if (yAxisOptions?.tickFormat) {
        yAxis.tickFormat(yAxisOptions?.tickFormat);
    }
    svg.append('g').style('font-size', '12px').call(yAxis);
    // Add Y-axis label
    svg.append('text')
        .attr('transform', 'rotate(-90)')
        .attr('y', 0 - margin.left)
        .attr('x', 0 - height / 2)
        .attr('dy', '1em')
        .style('text-anchor', 'middle')
        .style('font-size', '14px')
        .style('fill', '#777')
        .text(yAxisLabel);
    // Set color of the axis, ticks and labels
    svg.selectAll('.tick text').attr('fill', '#777');
    svg.selectAll('path.domain').attr('stroke', '#777');
    svg.selectAll('.tick line').attr('stroke', '#777');
    // Add horizontal and vertical gridlines
    addGridToSvg({ svg, x, xTicks, y, height, width });

    const addDynamicElements = () => {
        return addChartDynamicElementsToSvg({
            svg,
            height,
            width,
            containerId,
        });
    };

    return { svg, x, y, width, height, margin, addDynamicElements };
};

export type TimelineTemplateProps = {
    dataset: GenericObject[];
    xAxisKey: keyof GenericObject;
    extendCursorThroughAxis?: boolean;
    lines: { id: string }[];
    containerId: string;
    chartHeight?: number;
    chartWidth?: number;
    chartMargin?: Partial<MarginConfig>;
    dateRange?: DateRange | null;
    timeZone: UserDisplayPreferences['timeZone'];
};

/**
 * Creates a chart SVG element and appends it to the specified container.
 * Does things that need to be done only once for the entire chart:
 *    - create svg element
 *    - set up x timescale and y linear scale
 *    - define x and y domains
 *    - add x-axis
 *    - set color of the axis, ticks and labels
 *    - add horizontal gridlines
 *    - add hover elements: hoverLine, listeningRect, tooltip
 */
export const createTimelineTemplate = ({
    dataset,
    xAxisKey,
    lines,
    extendCursorThroughAxis,
    containerId,
    chartHeight,
    chartWidth,
    chartMargin,
    timeZone = 'local',
    dateRange,
}: TimelineTemplateProps) => {
    if (!dataset?.length && !dateRange) {
        return null;
    }
    // remove the previous chart if it exists
    const previousSvg = getSvgByContainerId(containerId);
    if (previousSvg) {
        previousSvg.remove();
    }
    // Set dimentions and margins for the chart
    // Create svg element (if containerId exists in the document) and append it to the chart container
    const element = createSvgElement(containerId, {
        height: chartHeight,
        width: chartWidth,
        margin: chartMargin,
    });
    if (!element) {
        return null;
    }
    const { svg, width, height, margin } = element;
    // Set up the x and y scales
    const x = d3.scaleTime().range([0, width]);
    const y = d3
        .scaleBand()
        .range([height / (lines.length * 2), height + height / (lines.length * 2)]) // shift the range half a band-width down from the top
        .domain(lines.map(({ id }) => id));
    /*
        Define the x and y domains.
        Note adding 30 minutes to the x-axis min and max values to make sure the chart is not cut off at the edges
        especially then the dots or bars are wide
     */
    if (!dateRange) {
        // Getting x axis range from the data to make sure the chart does not have any empty space and aligns with the dataset
        const [xDataMin, xDataMax] = d3.extent(dataset, (datum) => new Date(getXValue(datum, xAxisKey)));
        if (!xDataMin || !xDataMax) {
            console.warn(
                `Cannot create ${containerId} line chart. xDataMin or xDataMax are undefined, xDataMin: ${xDataMin}, xDataMax: ${xDataMax}`
            );
            return null;
        }
        x.domain([xDataMin, xDataMax]);
    } else {
        x.domain([dateRange.startDate, dateRange.endDate]);
    }
    padDomainInRangeUnits(x, 4, 4);

    // Add the x-axis
    const { ticks: xTicks } = addXAxisToSvg({ svg, x, width, height, timeZone });

    // Set color of the axis, ticks and labels
    svg.selectAll('.tick text').attr('fill', '#777');
    svg.selectAll('path.domain').attr('stroke', '#777');
    svg.selectAll('.tick line').attr('stroke', '#777');
    // Add horizontal and vertical gridlines
    addGridToSvg({ svg, x, xTicks, height, width });

    const cursorHeight = extendCursorThroughAxis ? height + margin.bottom : height;

    const addDynamicElements = () => {
        return addChartDynamicElementsToSvg({
            svg,
            height: cursorHeight,
            width,
            containerId,
        });
    };

    const drawExpansionBracket = (dateRange: DateRange | null, targetScale: d3.ScaleTime<number, number, never>) => {
        drawExpansionBracketToSvg({
            topXPx: isNil(dateRange) ? undefined : [x(dateRange.startDate) - 1, x(dateRange.endDate)],
            bottomXPx: isNil(dateRange)
                ? undefined
                : [targetScale(dateRange.startDate) - 1, targetScale(dateRange.endDate)],
            svg,
            containerId,
            width,
            height,
            margin,
        });
    };
    return { svg, x, y, width, height, margin, addDynamicElements, drawExpansionBracket };
};

type ContinuousBandChartTemplateProps = {
    xAxisBands: Date[];
    yAxisBands: number[];
    yAxisLabel: string;
    containerId: string;
    chartHeight?: number;
    chartWidth?: number;
    chartMargin?: Partial<MarginConfig>;
    valueKey: PropertyKey;
    xAxisBounds?: [Date, Date];
    showSeconds?: boolean;
    timeZone: UserDisplayPreferences['timeZone'];
};
/**
 * Creates a chart SVG element and appends it to the specified container.
 * Does things that need to be done only once for the entire chart:
 *    - create svg element
 *    - set up x and y scale as continuous band (different than the other charts)
 *    - define x and y domains
 *    - add x-axis
 *    - add y-axis
 *    - add y-axis label
 *    - set color of the axis, ticks and labels
 *    - add hover elements: hoverLine, listeningRect, tooltip
 */
export const createContinuousBandChartTemplate = ({
    xAxisBands,
    yAxisBands,
    yAxisLabel,
    containerId,
    chartHeight,
    chartWidth,
    showSeconds,
    timeZone,
    chartMargin,
}: ContinuousBandChartTemplateProps) => {
    // remove the previous chart if it exists
    const previousSvg = getSvgByContainerId(containerId);
    if (previousSvg) {
        previousSvg.remove();
    }
    chartMargin = { bottom: 78, ...chartMargin };
    // Set dimentions and margins for the chart
    // Create svg element (if containerId exists in the document) and append it to the chart container
    const element = createSvgElement(containerId, {
        height: chartHeight,
        width: chartWidth,
        margin: chartMargin,
    });
    if (!element) {
        return null;
    }
    const { svg, width, height, margin } = element;
    // Set up the x and y scales
    const timestamps = chain(xAxisBands)
        .map((d) => d.getTime())
        .sort()
        .uniq()
        .map((t) => new Date(t))
        .value();
    const x = d3
        .scaleBand()
        .domain(timestamps.map((d) => d.toISOString()))
        .range([0, width]);
    const invertX = d3.scaleQuantize<Date>().domain(x.range()).range(timestamps);

    const freqs = sortedUniq(d3.sort(yAxisBands));
    const y = d3
        .scaleBand()
        .domain(freqs.map((d) => d.toString()))
        .range([height, 0]);
    const invertY = d3.scaleQuantize<number>().domain(y.range().toReversed()).range(freqs.toReversed());

    // define x and y domains
    const [xAxisMin, xAxisMax] = d3.extent(timestamps);
    if (!xAxisMin || !xAxisMax) {
        console.warn(`Cannot create ${containerId} line chart. xAxisMin or xAxisMax are undefined`);
        return null;
    }

    const xTimeScale = d3.scaleTime().domain([xAxisMin, xAxisMax]).range([0, width]);

    // Add the x-axis
    addXAxisToSvg({ svg, x: xTimeScale, width, height: height, timeZone, showSeconds });

    // Add the y-axis
    svg.append('g').style('font-size', '12px').call(d3.axisLeft(y));
    // Add Y-axis label
    svg.append('text')
        .attr('transform', 'rotate(-90)')
        .attr('y', -3 - margin.left)
        .attr('x', 0 - height / 2)
        .attr('dy', '1em')
        .style('text-anchor', 'middle')
        .style('font-size', '14px')
        .style('fill', '#777')
        .text(yAxisLabel);
    // Set color of the axis, ticks and labels
    svg.selectAll('.tick text').attr('fill', '#777');
    svg.selectAll('path.domain').attr('stroke', '#777');
    svg.selectAll('.tick line').attr('stroke', '#777');

    // Add the missing data background pattern.
    const diagonalFillId = `diagonalFill-${containerId}`;
    const pattern = svg
        .append('defs')
        .append('pattern')
        .attr('id', diagonalFillId)
        .attr('width', 8)
        .attr('height', 8)
        .attr('patternTransform', 'rotate(45 0 0)')
        .attr('patternUnits', 'userSpaceOnUse');
    pattern.append('rect').attr('width', 8).attr('height', 8).style('fill', '#677E98');
    pattern
        .append('line')
        .attr('x1', '0')
        .attr('y1', '0')
        .attr('x2', '0')
        .attr('y2', '8')
        .style('stroke', '#7E92A8')
        .style('stroke-width', '8');

    svg.append('rect')
        .attr('width', width)
        .attr('height', height)
        .attr('fill', `url(#${diagonalFillId})`)
        .attr('id', `bgfill-${containerId}`);

    const addDynamicElements = () => {
        const { hoverLine, listeningRect, tooltip } = addChartDynamicElementsToSvg({
            svg,
            height,
            width,
            containerId,
            options: { listeningRectBehavior: 'lower' },
        });
        hoverLine.style('stroke-width', 1);
        hoverLine.style('stroke', BAND_CHART_HOVER_COLOR);
        hoverLine.style('opacity', 1);
        hoverLine.style('transform', `translate(${x.bandwidth() / 2}px, 0)`);

        const selectionLine = svg
            .append('g')
            .style('visibility', 'hidden')
            .style('stroke', BAND_CHART_HOVER_COLOR)
            .style('stroke-width', 1)
            .style('stroke-dasharray', '2 1')
            .style('pointer-events', 'none')
            .attr('id', `selectionline-${containerId}`);
        selectionLine.append('line').attr('x1', 0).attr('x2', 0).attr('y1', 0).attr('y2', height);
        selectionLine
            .append('line')
            .attr('x1', x.bandwidth() - 1)
            .attr('x2', x.bandwidth() - 1)
            .attr('y1', 0)
            .attr('y2', height);

        // remove the previous hoverbox if it exists
        const previousHoverBox = document.getElementById(`hoverbox-${containerId}`);
        if (previousHoverBox) {
            previousHoverBox.remove();
        }
        const hoverBox = svg
            .append('rect')
            .style('visibility', 'hidden')
            .attr('width', x.bandwidth())
            .attr('height', y.bandwidth())
            .style('stroke-width', '2px')
            .style('stroke', '#ddd')
            .style('pointer-events', 'none')
            .attr('id', `hoverbox-${containerId}`);

        return { selectionLine, hoverLine, hoverBox, listeningRect, tooltip };
    };

    const drawBandExpansionBracket = (bandIndex: number | null) => {
        drawExpansionBracketToSvg({
            topXPx: isNil(bandIndex) ? undefined : [bandIndex * x.bandwidth(), (bandIndex + 1) * x.bandwidth() - 1],
            svg,
            containerId,
            width,
            height,
            margin,
        });
    };

    return {
        svg,
        x,
        y,
        invertX,
        invertY,
        xTimeScale,
        width,
        height,
        margin,
        addDynamicElements,
        drawBandExpansionBracket,
    };
};

const drawExpansionBracketToSvg = ({
    topXPx,
    bottomXPx,
    svg,
    containerId,
    width,
    height,
    margin,
}: {
    topXPx?: [number, number];
    bottomXPx?: [number, number];
    svg: D3SvgSelection;
    containerId: string;
    width: number;
    height: number;
    margin: MarginConfig;
}) => {
    const id = `bandExpansionBracket-${containerId}`;
    svg.selectChild(`#${CSS.escape(id)}`)?.remove();

    if (!topXPx) return null;

    if (!bottomXPx) {
        bottomXPx = [0, width];
    }

    if (bottomXPx[0] < 0 || bottomXPx[1] > width) {
        // out of range, probably need to wait for underchart to get a fresh scale
        return null;
    }

    const bandExpansionBracket = svg
        .append('g')
        .style('stroke-dasharray', '2 1')
        .style('stroke-width', 1)
        .style('pointer-events', 'none')
        .style('transform', `translate(${topXPx[0]}px, ${height}px)`)
        .attr('id', id);
    const bracketHeight = margin.bottom;
    const upperOriginX = topXPx[1] - topXPx[0];
    const upperBracketSpan = bottomXPx[1] - topXPx[1];
    const lowerBracketSpan = topXPx[0] - bottomXPx[0];
    const upperBracketDirection = upperBracketSpan > 0 ? 1 : -1; // 1 if the top chart upper bound is left of the bottom chart upper bound, -1 if it slants /-way
    const lowerBracketDirection = lowerBracketSpan > 0 ? 1 : -1; // 1 if the top chart lower bound is right of the bottom chart lower bound, -1 if it slants \-way
    const upperBracketSpanY = bracketHeight - (upperBracketDirection === 1 ? 28 : 16);
    const lowerBracketSpanY = bracketHeight - (lowerBracketDirection === 1 ? 28 : 16);

    const lowerBracketSpanPoints =
        Math.abs(lowerBracketSpan) > 100
            ? `${-lowerBracketSpan + lowerBracketDirection * 50},${lowerBracketSpanY} ${-lowerBracketSpan / 2 - lowerBracketDirection * 25},${lowerBracketSpanY} ${-lowerBracketSpan / 2},${lowerBracketSpanY} ${lowerBracketDirection * -75},${lowerBracketSpanY} ${lowerBracketDirection * -50},${lowerBracketSpanY}`
            : `${-lowerBracketSpan / 2},${lowerBracketSpanY}`;

    const upperBracketSpanPoints =
        Math.abs(upperBracketSpan) > 100
            ? `${upperOriginX + upperBracketDirection * 50},${upperBracketSpanY} ${upperOriginX + upperBracketSpan / 2 - upperBracketDirection * 25},${upperBracketSpanY}  ${upperOriginX + upperBracketSpan / 2},${upperBracketSpanY} ${upperOriginX + upperBracketSpan - upperBracketDirection * 75},${upperBracketSpanY} ${upperOriginX + upperBracketSpan - upperBracketDirection * 50},${upperBracketSpanY}`
            : `${upperOriginX + upperBracketSpan / 2},${upperBracketSpanY}`;

    const bracketPath = (closed: boolean) =>
        `M ${-lowerBracketSpan},${bracketHeight} Q ${-lowerBracketSpan},${lowerBracketSpanY} ${lowerBracketSpanPoints} ${0},${lowerBracketSpanY} 0,0 ` +
        `${closed ? 'L' : 'M'} ${upperOriginX},0 Q ${upperOriginX},${upperBracketSpanY} ${upperBracketSpanPoints} ${upperOriginX + upperBracketSpan},${upperBracketSpanY} ${upperOriginX + upperBracketSpan},${bracketHeight} ` +
        (closed ? 'Z' : '');

    bandExpansionBracket.append('path').style('stroke', 'none').style('fill', '#cccccc88').attr('d', bracketPath(true));
    bandExpansionBracket.append('path').style('stroke', '#888888').style('fill', 'none').attr('d', bracketPath(false));

    return bandExpansionBracket;
};

export const addCompassLabelsToRadialChart = (
    svgElement: d3.Selection<SVGGElement, unknown, HTMLElement, unknown>,
    radius: number
) => {
    ['N', 'E', 'S', 'W'].forEach((label, i) => {
        const angle = (Math.PI / 180) * (i * 90 - 90);
        const x = Math.cos(angle) * (radius + 12);
        const y = Math.sin(angle) * (radius + 12);
        svgElement
            .append('text')
            .attr('x', x)
            .attr('y', y)
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'middle')
            .style('font-size', '10px')
            .style('fill', 'grey')
            .text(label);
    });
};

export const addRadialSpokesToRadialChart = (
    svgElement: d3.Selection<SVGGElement, unknown, HTMLElement, unknown>,
    radius: number,
    degreeInterval: number = 30,
    attrs?: { [name: string]: SvgSelectionAttr }
) => {
    const attrsWithDefaults = {
        stroke: 'grey',
        'stroke-width': GRID_LINE_STROKE_WIDTH,
        opacity: 0.6,
        ...attrs,
    };

    const radialLines = d3.range(0, 360, degreeInterval);
    radialLines.forEach((deg) => {
        const angle = (Math.PI / 180) * deg;
        const x = Math.cos(angle) * radius;
        const y = Math.sin(angle) * radius;
        const spoke = svgElement.append('line').attr('x1', 0).attr('y1', 0).attr('x2', x).attr('y2', y);
        map(attrsWithDefaults, (value, name) => spoke.attr(name, value));
    });
};

export const createRadialChartTemplate = ({
    containerId,
    chartHeight,
    chartWidth,
    compassLabels,
}: {
    containerId: string;
    chartHeight: number;
    chartWidth: number;
    compassLabels: boolean;
}) => {
    // remove the previous chart if it exists
    const previousSvg = getSvgByContainerId(containerId);
    if (previousSvg) {
        previousSvg.remove();
    }
    // Set dimentions and margins for the chart
    // Create svg element and append it to the chart container
    const svg = d3
        .select(`#${CSS.escape(containerId)}`)
        .append('svg')
        .attr('width', chartWidth)
        .attr('height', chartHeight)
        .style('font-family', fontFamily)
        .style('font-weight', 300);

    const grid = svg.append('g').attr('transform', `translate(${chartWidth / 2},${chartWidth / 2})`);
    const radius = Math.min(chartWidth, chartHeight) / 2 - (compassLabels ? 20 : 2);
    // background
    grid.append('circle').style('fill', 'white').attr('r', radius).attr('stroke', 'grey').attr('stroke-width', 1);

    // compass labels
    if (compassLabels) {
        const compassGroup = grid.append('g');
        addCompassLabelsToRadialChart(compassGroup, radius);
    }

    const appendGridRingsAndSpokes = (radii: number[], attrs?: { [name: string]: SvgSelectionAttr }) => {
        const attrsWithDefaults = { stroke: 'grey', 'stroke-width': GRID_LINE_STROKE_WIDTH, opacity: 0.6, ...attrs };

        // radial grid lines
        radii.forEach((r) => {
            const ring = grid.append('circle').attr('r', r).attr('fill', 'none');
            map(attrsWithDefaults, (value, name) => ring.attr(name, value));
        });

        // outermost radial line
        const outerRing = grid
            .append('circle')
            // so the outermost line is a bit larger and is still visible as a circle border even if there is data arc in the last freq bin
            .attr('r', radius + GRID_LINE_STROKE_WIDTH * 1.5)
            .attr('fill', 'none');
        map({ ...attrsWithDefaults, 'stroke-width': attrsWithDefaults['stroke-width'] * 1.5 }, (value, name) =>
            outerRing.attr(name, value)
        );

        addRadialSpokesToRadialChart(grid, radius, 22.5, attrs);
    };

    return {
        svg,
        grid,
        width: chartWidth,
        height: chartHeight,
        radius,
        appendGridRingsAndSpokes,
    };
};

export const addAlertLinesToSvg = ({
    svg,
    y,
    width,
    height,
    alertLines,
}: {
    svg: D3SvgSelection;
    y: d3.ScaleLinear<number, number>;
    width: number;
    height: number;
    alertLines: AlertLine[];
}) => {
    try {
        alertLines.forEach(({ value, operator, label, severity = 'warning' }) => {
            if (!isFiniteNumber(value)) {
                return;
            }
            const yValue = y(value);
            if (yValue <= 0 || yValue >= height) {
                return;
            }

            const thresholdLine = svg
                .append('line')
                .style('stroke', ALERT_SEVERITY_COLORS[severity].line)
                .style('stroke-width', ALERT_LINE_STROKE_WIDTH)
                .style('stroke-dasharray', 2)
                .attr('opacity', 0.5)
                .attr('x1', 0)
                .attr('x2', width)
                .attr('y1', yValue)
                .attr('y2', yValue);

            // add a text label at the right edge of the alert line
            const labelObject = svg
                .append('foreignObject')
                .attr('x', width)
                .attr('y', yValue)
                .attr('width', 1) // we really want zero but firefox won't position it unless it has dimensions
                .attr('height', 1)
                .style('opacity', 0.5)
                .style('transition', 'opacity 0.2s ease-in-out')
                .style('overflow', 'visible');

            const labelDiv = labelObject
                .append('xhtml:div')
                .style('display', 'inline-block')
                .style('padding', '4px')
                .style('padding-left', '6px')
                .style('padding-right', '6px')
                .style('font-size', '12px')
                .style('color', ALERT_SEVERITY_COLORS[severity].label)
                .style('background-color', ALERT_SEVERITY_COLORS[severity].bg)
                .style('border-radius', '2px')
                .style('cursor', 'default')
                .style('white-space', 'nowrap')
                .on('mouseover', () => {
                    labelObject.style('opacity', 1);
                    thresholdLine.style('opacity', 1);
                })
                .on('mouseout', () => {
                    labelObject.style('opacity', 0.7);
                    thresholdLine.style('opacity', 0.7);
                })
                .text((label ? `${label}: ` : '') + `${prettyOperator(operator)} ${value}`);

            if (isGreater(operator)) {
                labelDiv.style('transform', 'translateX(-100%) translateY(calc(-100% - 2px))');
            } else {
                labelDiv.style('transform', 'translateX(-100%) translateY(2px)');
            }
        });
    } catch (err) {
        console.error('Error adding alert lines', err);
    }
};

export const getSvgByContainerId = (containerId: string) =>
    document.getElementById(containerId)?.getElementsByTagName('svg')[0];

/**
 * Grabs the nodes a chart owns and returns a teardown that takes them out of the document. Charts are
 * rebuilt from scratch on every data update, so an SVG that is merely orphaned keeps its whole tree —
 * and the chart object d3 bound to it — reachable.
 *
 * Call this at draw time, not at teardown time. React detaches a component's DOM during the mutation
 * phase, before it runs the effect cleanups, so by the time a cleanup fires the container is no longer
 * findable by id and looking it up then would silently do nothing.
 */
export const captureChartTeardown = (containerId: string) => {
    const container = document.getElementById(containerId);
    const svgNodes = container ? Array.from(container.getElementsByTagName('svg')) : [];
    // the tooltip is a sibling div rather than part of the svg, so it has to be tracked separately
    const tooltipNode = document.getElementById(`tooltip-${containerId}`);

    return () => {
        svgNodes.forEach((svg) => svg.remove());
        tooltipNode?.remove();
    };
};

/**
 * Holds the latest value a drag has reached and reports it at most once per frame.
 *
 * A pointer emits moves faster than a frame, and a chart's timestamp selection is a React state change
 * that redraws whatever is keyed on the cursor. Hover effects are drawn directly on each move, so the
 * coalescing is not visible in the chart itself.
 *
 * `cancel` belongs in the chart's `destroy`, so that a frame scheduled by the last move before a teardown
 * cannot report into a callback whose component has gone.
 */
export const frameCoalescedReporter = <TValue>(report: (value: TValue) => void) => {
    // boxed so that a value of `undefined` is still a value to report, distinct from having nothing to send
    let pending: { value: TValue } | null = null;
    let frame: number | null = null;

    return {
        send(value: TValue) {
            pending = { value };
            if (frame !== null) {
                return;
            }
            frame = requestAnimationFrame(() => {
                frame = null;
                const delivered = pending;
                pending = null;
                if (delivered) {
                    report(delivered.value);
                }
            });
        },

        cancel() {
            if (frame !== null) {
                cancelAnimationFrame(frame);
                frame = null;
            }
            pending = null;
        },
    };
};

/**
 * Report one user interaction for each mouse press on a chart, one animation frame after the button is
 * released anywhere in the window. The source is `'drag'` when the pointer reached a different timestamp
 * while the button was down, and `'click'` otherwise. A press on the selected timestamp that does not move
 * clears the selection, so it reports nothing.
 *
 * The report waits for that frame because the last drag position reaches `onSelectTimestamp` through
 * `frameCoalescedReporter` in an animation frame. Frames run in the order they were scheduled, so
 * `onSelectTimestamp` always runs before the report.
 *
 * `cancel` belongs in the chart's `destroy`, so that a button released after a teardown reports nothing.
 * A report that is already scheduled still runs: the user released the button before the teardown.
 */
export const pointerGestureReporter = (report: (source: PointerInteractionSource) => void) => {
    let gesture: { pressedTimestamp: string; isOnSelection: boolean; hasMoved: boolean } | null = null;

    const handleMouseUp = () => {
        window.removeEventListener('mouseup', handleMouseUp);
        const endedGesture = gesture;
        gesture = null;
        if (!endedGesture || (!endedGesture.hasMoved && endedGesture.isOnSelection)) {
            return;
        }
        const source = endedGesture.hasMoved ? 'drag' : 'click';
        requestAnimationFrame(() => report(source));
    };

    const cancel = () => {
        window.removeEventListener('mouseup', handleMouseUp);
        gesture = null;
    };

    return {
        press(timestamp: string, { isOnSelection }: { isOnSelection: boolean }) {
            cancel();
            gesture = { pressedTimestamp: timestamp, isOnSelection, hasMoved: false };
            window.addEventListener('mouseup', handleMouseUp);
        },

        drag(timestamp: string) {
            if (gesture && timestamp !== gesture.pressedTimestamp) {
                gesture.hasMoved = true;
            }
        },

        cancel,
    };
};
